/**
 * Product Card Component
 * Componente reutilizable para generar HTML de product cards
 * Compatible con la estructura del snippet product-card.liquid
 */

(function() {
  'use strict';

  // Función helper para formatear dinero usando el formato de Shopify
  function formatMoney(cents) {
    if (cents === null || cents === undefined || cents === 0) return '';
    
    // Convertir a número si es string
    let numCents = typeof cents === 'string' ? parseFloat(cents) : cents;
    if (isNaN(numCents) || numCents <= 0) return '';
    
    const currencyCode = window.currencyCode || '';
    
    // Determinar si la moneda usa centavos o no
    const currenciesWithoutCents = ['CLP', 'JPY', 'KRW', 'VND', 'VUV', 'XAF', 'XOF', 'XPF'];
    const useCents = !currenciesWithoutCents.includes(currencyCode);
    
    // Calcular el monto: dividir por 100 solo si la moneda usa centavos
    const amount = useCents ? numCents / 100 : numCents;
    
    // Formatear el número: punto para miles, coma para decimales (formato europeo/latino)
    function formatWithDelimiters(number, precision = 0, thousands = '.', decimal = ',') {
      if (isNaN(number) || number == null) return '0';
      
      // Para CLP y monedas sin centavos, no mostrar decimales
      const finalPrecision = currenciesWithoutCents.includes(currencyCode) ? 0 : precision;
      
      number = parseFloat(number).toFixed(finalPrecision);
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const centsAmount = parts[1] && finalPrecision > 0 ? decimal + parts[1] : '';
      
      return dollarsAmount + centsAmount;
    }
    
    // Obtener el formato de dinero de Shopify para mantener el símbolo de moneda
    const moneyFormat = window.moneyFormat || '{{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = moneyFormat.match(placeholderRegex);
    
    // Formatear con punto para miles y coma para decimales
    const formattedAmount = formatWithDelimiters(amount, 2, '.', ',');
    
    if (match) {
      return moneyFormat.replace(placeholderRegex, formattedAmount);
    }
    
    // Si no hay formato, usar el símbolo de moneda por defecto
    return formattedAmount;
  }

  // Función helper para obtener precio y compare_at_price
  function getProductPrices(product) {
    let currentPrice = 0;
    let compareAtPrice = null;
    
    const toNumber = (val) => {
      if (val === null || val === undefined) return 0;
      const num = typeof val === 'string' ? parseFloat(val) : val;
      return isNaN(num) ? 0 : num;
    };
    
    // PRIORIZAR: Buscar precio directamente en las variantes
    if (product.variants && product.variants.length > 0) {
      const firstAvailableVariant = product.variants.find(v => v.available && toNumber(v.price) > 0);
      const firstVariant = firstAvailableVariant || product.variants.find(v => toNumber(v.price) > 0);
      
      if (firstVariant && toNumber(firstVariant.price) > 0) {
        currentPrice = toNumber(firstVariant.price);
      }
    }
    
    // Fallback: usar price_min o price del producto
    if (currentPrice === 0) {
      const priceMin = toNumber(product.price_min);
      const price = toNumber(product.price);
      
      if (priceMin > 0) {
        currentPrice = priceMin;
      } else if (price > 0) {
        currentPrice = price;
      }
    }
    
    // Buscar compare_at_price - PRIORIZAR variantes
    if (product.variants && product.variants.length > 0) {
      for (const variant of product.variants) {
        const variantCompare = toNumber(variant.compare_at_price);
        if (variantCompare > 0) {
          compareAtPrice = variantCompare;
          break;
        }
      }
    }
    
    // Fallback: usar compare_at_price del producto
    if (!compareAtPrice || compareAtPrice === 0) {
      const comparePrice = toNumber(product.compare_at_price);
      const comparePriceMin = toNumber(product.compare_at_price_min);
      
      if (comparePrice > 0) {
        compareAtPrice = comparePrice;
      } else if (comparePriceMin > 0) {
        compareAtPrice = comparePriceMin;
      }
    }
    
    const showStrikethrough = compareAtPrice && compareAtPrice > 0 && currentPrice > 0;
    
    return {
      current: currentPrice,
      compare: compareAtPrice,
      hasDiscount: showStrikethrough
    };
  }

  function normalizeColorValue(colorValue) {
    if (!colorValue) return '';
    if (typeof colorValue === 'string') return colorValue;
    if (typeof colorValue === 'object') {
      if (colorValue.value) return String(colorValue.value);
      if (colorValue.name) return String(colorValue.name);
    }
    return String(colorValue);
  }

  // Mapeo básico de nombres de color a hex (sin imágenes)
  function getColorHex(colorValue) {
    if (!colorValue) return '#D9D9D9';
    if (typeof colorValue === 'object') {
      if (colorValue.swatch && colorValue.swatch.color && colorValue.swatch.color.rgb) {
        return `rgb(${colorValue.swatch.color.rgb})`;
      }
    }

    const colorName = normalizeColorValue(colorValue);
    if (!colorName) return '#D9D9D9';
    if (colorName.startsWith('#')) return colorName;
    const colorLower = colorName.toLowerCase().trim();
    const colorMap = {
      negro: '#000000',
      black: '#000000',
      navy: '#4A90E2',
      azul: '#4A90E2',
      blue: '#4A90E2',
      verde: '#4CAF50',
      green: '#4CAF50',
      rojo: '#F44336',
      red: '#F44336',
      blanco: '#FFFFFF',
      white: '#FFFFFF',
      cream: '#FFF8DC',
      gris: '#808080',
      gray: '#808080',
      grey: '#808080',
      pink: '#FFC0CB',
      rosa: '#FFC0CB',
      café: '#8B4513',
      brown: '#8B4513',
      marron: '#8B4513'
    };
    for (const [key, hex] of Object.entries(colorMap)) {
      if (colorLower.includes(key)) {
        return hex;
      }
    }
    return '#D9D9D9';
  }

  // Función para generar color swatch
  function generateColorSwatch(colorValue, productVariants, size = 10, border = '1px solid rgba(255, 255, 255, 0.8)') {
    const bgColor = getColorHex(colorValue);
    const colorName = normalizeColorValue(colorValue);
    const background = bgColor;
    
    return `
      <div 
        class="product-card__color-swatch color-swatch"
        data-color-name="${colorName}"
        data-color-value="${colorName}"
        style="width: ${size}px; height: ${size}px; border-radius: 50%; border: ${border}; background-color: ${background}; background-image: none !important; background: ${background} !important; cursor: pointer; transition: transform 0.2s ease; position: relative; overflow: hidden;"
        onmouseover="this.style.transform='scale(1.1)'"
        onmouseout="this.style.transform='scale(1)'"
        title="${colorName}"
      ></div>
    `;
  }

  // Función para inyectar estilos CSS si no existen
  function injectStyles() {
    // Estilos para flechas de navegación
    if (!document.getElementById('product-card-nav-styles')) {
      const styleElement = document.createElement('style');
      styleElement.id = 'product-card-nav-styles';
      styleElement.textContent = `
        .product-card__image-wrapper:hover .product-card__nav,
        .product-card:hover .product-card__nav {
          opacity: 1 !important;
          pointer-events: auto !important;
        }
        .product-card:hover .product-card__quick-add {
          opacity: 1 !important;
          pointer-events: auto !important;
        }
        .product-card__image-container img {
          pointer-events: none !important;
        }
        .product-card__image-container {
          pointer-events: auto !important;
        }
        .product-card__title a {
          pointer-events: auto !important;
        }
      `;
      document.head.appendChild(styleElement);
    }

    // Estilos para quick-add button
    if (!document.getElementById('product-card-quick-add-styles')) {
      const styleElement = document.createElement('style');
      styleElement.id = 'product-card-quick-add-styles';
      styleElement.textContent = `
        .product-card__quick-add-button {
          transition: width 0.25s ease, padding 0.25s ease, justify-content 0.25s ease, border-radius 0.25s ease;
          overflow: hidden;
          white-space: nowrap;
          gap: 8px;
          align-items: center;
          justify-content: center;
          background-color: transparent !important;
          box-shadow: none !important;
        }
        .product-card__quick-add-button svg {
          display: block;
          flex-shrink: 0;
          margin: 0;
        }
        .product-card__quick-add-button .quick-add-pill__label {
          white-space: nowrap;
          font-size: 14px;
          opacity: 0;
          max-width: 0;
          transform: translateX(-4px);
          transition: opacity 0.25s ease, max-width 0.25s ease, transform 0.25s ease, margin-left 0.25s ease;
          margin-left: 0;
          color: rgba(0, 0, 0, 0.81);
          line-height: 1;
          display: flex;
          align-items: center;
        }
        .product-card__quick-add-button:hover {
          width: auto !important;
          min-width: auto !important;
          padding: 8px 11.2px !important;
          justify-content: flex-start !important;
          border-radius: 999px !important;
        }
        .product-card__quick-add-button:hover .quick-add-pill__label {
          opacity: 1;
          max-width: 60px;
          transform: translateX(0);
          margin-left: 4.8px;
        }
      `;
      document.head.appendChild(styleElement);
    }
  }

  // Inicializar colores sólidos en swatches de product cards (NO en main product page)
  function initializeProductCardColorSwatches() {
    // Evitar ejecución en la página de producto con selectors de variantes
    if (document.querySelector('.product-variant-selector__color-button')) {
      return;
    }

    if (typeof window.ProductColorMapper === 'undefined') {
      setTimeout(initializeProductCardColorSwatches, 100);
      return;
    }

    const colorSwatches = document.querySelectorAll(
      '.product-card__color-swatch, .color-swatch, .product-card__color-swatches .color-swatch, .product-card__color-swatches-info .color-swatch'
    );

    colorSwatches.forEach(function (swatch) {
      const colorName =
        swatch.getAttribute('data-color-name') ||
        swatch.getAttribute('data-color-value') ||
        swatch.getAttribute('title') ||
        swatch.getAttribute('aria-label');

      if (!colorName) return;

      const images = swatch.querySelectorAll('img, picture, [class*="image"]');
      images.forEach(function (img) {
        img.style.setProperty('display', 'none', 'important');
        img.style.setProperty('visibility', 'hidden', 'important');
        img.style.setProperty('opacity', '0', 'important');
        img.style.setProperty('width', '0', 'important');
        img.style.setProperty('height', '0', 'important');
        if (img.parentNode) {
          img.remove();
        }
      });

      swatch.style.removeProperty('background');
      swatch.style.removeProperty('background-image');
      swatch.style.removeProperty('background-size');
      swatch.style.removeProperty('background-position');
      swatch.style.removeProperty('background-repeat');

      const colorHex = window.ProductColorMapper.getColor(colorName);
      if (colorHex) {
        swatch.style.setProperty('background-color', colorHex, 'important');
        swatch.style.setProperty('display', 'block', 'important');
        swatch.style.setProperty('visibility', 'visible', 'important');
        swatch.style.setProperty('opacity', '1', 'important');
        swatch.style.setProperty('border-radius', '50%', 'important');
      }
    });
  }

  /**
   * Genera el HTML de un product card
   * @param {Object} product - Objeto del producto desde Shopify API
   * @param {Object} options - Opciones de configuración
   * @param {boolean} options.showBestSellerTag - Mostrar tag "BESTSELLERS" (default: true)
   * @param {string} options.quickAddButtonBgColor - Color de fondo del botón quick-add (default: '#FFFFFF')
   * @returns {string} HTML del product card
   */
  window.renderProductCard = function(product, options = {}) {
    if (!product) return '';

    const {
      showBestSellerTag = true,
      quickAddButtonBgColor = '#FFFFFF'
    } = options;

    // Inyectar estilos si no existen
    injectStyles();

    let images = Array.isArray(product.images) ? product.images : [];
    if (images.length === 0 && product.featured_image) {
      images = [product.featured_image];
    }
    const prices = getProductPrices(product);
    const productHandle = product.handle;
    const productUrl = `/products/${productHandle}`;

    // Detectar si es BESTSELLER
    let isBestSeller = false;
    try {
      if (product.tags) {
        let productTagsStr = '';
        if (Array.isArray(product.tags)) {
          productTagsStr = product.tags.join(',').toLowerCase();
        } else if (typeof product.tags === 'string') {
          productTagsStr = product.tags.toLowerCase();
        }
        if (productTagsStr) {
          isBestSeller = productTagsStr.includes('best-seller') || 
                       productTagsStr.includes('BESTSELLER') || 
                       productTagsStr.includes('bestseller') || 
                       productTagsStr.includes('bestsellers');
        }
      }
    } catch (e) {
      isBestSeller = false;
    }

    // Detectar si es nuevo
    let isNew = false;
    try {
      if (product.tags) {
        let productTagsStr = '';
        if (Array.isArray(product.tags)) {
          productTagsStr = product.tags.join(',').toLowerCase();
        } else if (typeof product.tags === 'string') {
          productTagsStr = product.tags.toLowerCase();
        }
        if (productTagsStr) {
          isNew = productTagsStr.includes('new') || 
                  productTagsStr.includes('new-in') || 
                  productTagsStr.includes('nuevo') || 
                  productTagsStr.includes('novedad');
        }
      }
    } catch (e) {
      isNew = false;
    }

    // Detectar si es BACK IN STOCK
    let isBackInStock = false;
    try {
      if (product.tags) {
        let productTagsStr = '';
        if (Array.isArray(product.tags)) {
          productTagsStr = product.tags.join(',').toLowerCase();
        } else if (typeof product.tags === 'string') {
          productTagsStr = product.tags.toLowerCase();
        }
        if (productTagsStr) {
          isBackInStock = productTagsStr.includes('back-in-stock') || 
                          productTagsStr.includes('back in stock') || 
                          productTagsStr.includes('backinstock');
        }
      }
    } catch (e) {
      isBackInStock = false;
    }

    // Generar HTML de imágenes
    let imagesHtml = '';
    if (images.length > 0) {
      imagesHtml = images.map((img, index) => {
        const opacity = index === 0 ? '1' : '0';
        const zIndex = index === 0 ? '2' : '1';
        const hiddenClass = index === 0 ? '' : ' product-card__image--hidden';
        return `<img src="${img.src || img}" alt="${(product.title || '').replace(/"/g, '&quot;')}" width="400" height="400" loading="${index === 0 ? 'eager' : 'lazy'}" class="product-card__image${hiddenClass}" data-image-index="${index}" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover; object-position: center; transition: opacity 0.25s ease; opacity: ${opacity}; z-index: ${zIndex}; pointer-events: none;">`;
      }).join('');
    } else {
      imagesHtml = '<div style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; background-color: rgba(0, 0, 0, 0.1); display: flex; align-items: center; justify-content: center;"></div>';
    }

    // Sold out: todas las variantes no disponibles
    const isSoldOut = product.variants && product.variants.length > 0 && !product.variants.some(function(v) { return v.available; });

    // Tags (AGOTADO > NEW IN > BESTSELLER; mismo estilo que product__media-soldout en galería)
    const soldOutTag = isSoldOut ? `
      <div class="product-card__soldout product__media-soldout" style="position: absolute; top: 16px; left: 16px; z-index: 10; background: transparent; border: none; padding: 0; font-size: 11px; font-weight: 600;">
        AGOTADO
      </div>
    ` : '';

    const newTag = !isSoldOut && isNew ? `
      <div class="product-card__new-tag" style="position: absolute; top: 16px; left: 16px; z-index: 10; background: transparent; border: none; padding: 0; font-size: 11px; font-weight: 600;">
        NEW IN
      </div>
    ` : '';

    const backInStockTag = !isSoldOut && !isNew && isBackInStock ? `
      <div class="product-card__back-in-stock-tag" style="position: absolute; top: 16px; left: 16px; z-index: 10; background: transparent; border: none; padding: 0; font-size: 11px; font-weight: 600;">
        BACK IN STOCK
      </div>
    ` : '';

    const bestSellerTag = !isSoldOut && !isNew && !isBackInStock && showBestSellerTag && isBestSeller ? `
      <div class="product-card__best-seller-tag" style="position: absolute; top: 16px; left: 16px; z-index: 10; background: transparent; border: none; padding: 0; font-size: 11px; font-weight: 600;">
        BESTSELLER
      </div>
    ` : '';

    const tagHtml = soldOutTag || newTag || backInStockTag || bestSellerTag;

    // Flechas de navegación (solo si hay más de 1 imagen)
    const navigationArrows = images.length > 1 ? `
      <button 
        type="button" 
        class="product-card__nav product-card__nav--prev" 
        aria-label="Imagen anterior"
        style="position: absolute; left: 12px; top: 50%; transform: translateY(-50%); z-index: 5; width: 40px; height: 40px; background-color: rgba(255, 255, 255, 0.9); border: none; border-radius: 50%; cursor: pointer; padding: 8px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); transition: all 0.2s ease; opacity: 0; pointer-events: none;">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 18L9 12L15 6" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <button 
        type="button" 
        class="product-card__nav product-card__nav--next" 
        aria-label="Imagen siguiente"
        style="position: absolute; right: 12px; top: 50%; transform: translateY(-50%); z-index: 5; width: 40px; height: 40px; background-color: rgba(255, 255, 255, 0.9); border: none; border-radius: 50%; cursor: pointer; padding: 8px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); transition: all 0.2s ease; opacity: 0; pointer-events: none;">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 18L15 12L9 6" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    ` : '';

    // Buscar opción de color
    let colorOption = null;
    let colorOptionIndex = -1;
    let colorValues = [];
    let colorSwatchesHtml = '';

    if (product.options && product.options.length > 0 && product.variants && product.variants.length > 0) {
      product.options.forEach((option, index) => {
        let optionName = '';
        
        if (typeof option === 'string') {
          optionName = option.toLowerCase();
        } else if (option && option.name) {
          optionName = option.name.toLowerCase();
        }
        
        if (optionName.includes('color') || optionName.includes('colour')) {
          colorOption = option;
          colorOptionIndex = index;
          
          if (option && option.values && Array.isArray(option.values)) {
            colorValues = option.values;
          } else {
            const colorSet = new Set();
            product.variants.forEach(variant => {
              let colorValue = null;
              if (index === 0 && variant.option1) {
                colorValue = variant.option1;
              } else if (index === 1 && variant.option2) {
                colorValue = variant.option2;
              } else if (index === 2 && variant.option3) {
                colorValue = variant.option3;
              }
              if (colorValue) {
                colorSet.add(colorValue);
              }
            });
            colorValues = Array.from(colorSet);
          }
        }
      });
      
      // Generar swatches de color
      if (colorOption && colorValues.length > 0) {
        const uniqueColors = [...new Set(colorValues.map(normalizeColorValue))];
        
        if (uniqueColors.length > 0) {
          colorSwatchesHtml = `
            <div class="product-card__color-swatches-info" style="display: flex; gap: 4px; flex-wrap: wrap; margin-top: 4px;">
              ${uniqueColors.map(colorValue => generateColorSwatch(colorValue, product.variants, 10, '1px solid rgba(0, 0, 0, 0.1)')).join('')}
            </div>
          `;
        }
      }
    }

    // Quick-add button (solo cruz, sin fondo)
    const quickAddButton = `
      <div class="product-card__quick-add" style="position: absolute; bottom: 12px; right: 12px; z-index: 15; opacity: 0; pointer-events: none; transition: opacity 0.2s ease;">
        <button 
          type="button"
          class="product-card__quick-add-button"
          data-product-id="${product.id}"
          data-product-handle="${productHandle}"
          style="width: 32px; height: 32px; background-color: transparent; border: none; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 0; min-width: 32px;">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 5V19M5 12H19" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span class="quick-add-pill__label">Añadir</span>
        </button>
      </div>
    `;

    // Generar HTML completo
    return `
      <div class="product-card" data-product-handle="${productHandle}" style="display: flex; flex-direction: column; gap: 8px; width: 100%;">
        <div class="product-card__image-wrapper" style="position: relative; overflow: hidden; width: 100%; padding-top: 149.5%; border-radius: 0;">
          ${tagHtml}
          ${navigationArrows}
          <div class="product-card__image-container" style="position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: auto;">
            <a href="${productUrl}" style="display: block; position: absolute; top: 0; left: 0; width: 100%; height: 100%;">
              ${imagesHtml}
            </a>
          </div>
          ${quickAddButton}
        </div>
        <div class="product-card__info" style="padding: 0; display: flex; flex-direction: column; gap: 4px;">
          <h3 class="product-card__title" style="margin: 0; font-size: 12px; font-weight: 400;">
            <a href="${productUrl}" style="color: #171717; text-decoration: none;">
              ${product.title || ''}
            </a>
          </h3>
          <div class="product-card__price" style="font-size: 12px; font-weight: 400; display: flex; gap: 0.5rem; color: #5c5c5c;">
          ${prices.current > 0 ? `
            <span style="font-size: 12px;">
            ${formatMoney(prices.current)}
            </span>
            ` : ''}
            ${prices.compare && prices.compare > 0 ? `
              <span style="text-decoration: line-through; color: #A9AEB2; font-size: 12px;">
                ${formatMoney(prices.compare)}
              </span>
            ` : ''}
          </div>
          ${colorSwatchesHtml}
        </div>
      </div>
    `;
  };

  // Exportar funciones helper para uso externo si es necesario
  window.ProductCardComponent = {
    render: window.renderProductCard,
    formatMoney: formatMoney,
    getProductPrices: getProductPrices,
    generateColorSwatch: generateColorSwatch
  };

  // Exportar función globalmente y ejecutar periódicamente para product cards
  window.initializeProductCardColorSwatches = initializeProductCardColorSwatches;

  setInterval(function () {
    if (typeof window.ProductColorMapper !== 'undefined') {
      initializeProductCardColorSwatches();
    }
  }, 500);

  document.addEventListener('DOMContentLoaded', function () {
    initializeProductCardColorSwatches();
    setTimeout(initializeProductCardColorSwatches, 100);
    setTimeout(initializeProductCardColorSwatches, 500);
    setTimeout(initializeProductCardColorSwatches, 1000);
  });

})();

